import { lockClosed, person, eye, eyeOff } from "ionicons/icons";
import { useState } from "react";
import { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FormGroup, TextField } from "../../../atoms/LoginTextField";
import { useFormik } from "formik";
import * as Yup from "yup";
import FormButton from "../../../atoms/FormButton";
import { IdentificationMethodEnum } from "../../../../types/profile";
import ForgotPwdModal from "../../../../pages/Profile/ForgotPasswordModal";
import Gap from "../../../atoms/Gap";
import { styled } from "styled-components";

interface loginLoginFieldsProps {
  onConnect(arg: any): void;
  withNickName?: boolean;
  loginPlaceholder?: string;
}

interface FormType {
  login: string;
  password: string;
}

const EmailLoginFields: FC<loginLoginFieldsProps> = ({
  onConnect,
  withNickName,
  loginPlaceholder,
}): ReactElement => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [isForgotPwdModalOpen, setIsForgotPwdModalOpen] = useState(false);


  const loginSchema = Yup.object().shape({
    login: withNickName
      ? Yup.string().required()
      : Yup.string().email().required(),
    password: Yup.string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])(?!.*\s).{8,100}$/,
        "form_error_email_password"
      )
      .required(),
  });

  const connectUser = async (formData: FormType) => {
    try {
      onConnect({
        [IdentificationMethodEnum.PASSWORD_SIGNIN]: {
          login: withNickName
            ? formData.login?.trim().toLowerCase()
            : formData.login?.trim(),
          password: formData.password,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const { values, touched, errors, submitForm, setFieldValue } =
    useFormik<FormType>({
      initialValues: {
        login: "",
        password: "",
      },
      onSubmit: connectUser,
      validationSchema: loginSchema,
    });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submitForm();
      }}
      style={{ width: "100%" }}
      autoComplete="on"
    >
      <FormGroup>
        <TextField
          value={values.login}
          icon={person}
          type={withNickName ? "text" : "email"}
          error={touched.login && errors.login}
          onChange={(value) => setFieldValue("login", value)}
          placeholder={loginPlaceholder ?? t(
            withNickName
              ? "home_login_nickname_placeholder"
              : "home_login_email_placeholder"
          )}
        />

        <TextField
          value={values.password}
          icon={lockClosed}
          type={showPassword ? "text" : "password"}
          error={touched.password && errors.password}
          onChange={(value) => setFieldValue("password", value)}
          placeholder={t("home_login_password_placeholder")}
          autoComplete="current-password"
          rightIcon={showPassword ? eyeOff : eye}
          onRightIconClick={() => setShowPassword((prev) => !prev)}
        />
      </FormGroup>
        <div
            onClick={() => setIsForgotPwdModalOpen(true)}
            style={{
              textAlign: "right",
              cursor: "pointer",
              textDecoration: "underline",
              marginBottom: "20px",
              paddingRight: "50px",
            }}
          >
            {t("home_forgot_password")}
          </div>

           <ForgotPwdModal
          isOpen={isForgotPwdModalOpen}
          onDismiss={() => setIsForgotPwdModalOpen(false)}
        />
      <StyledFormButton type="submit">{t("home_login_button")}</StyledFormButton>
    </form>
  );
};

export default EmailLoginFields;

/* Login submit is a CTA — secondary family like every action button (the old
   primary-blue override predates the role semantics in src/theme/tokens.ts). */
const StyledFormButton = styled(FormButton)`
  background-color: var(--app-color-secondary, #fecf48) !important;
  color: var(--app-color-secondary-contrast, #041723) !important;
`;